var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { defineComponent as _defineComponent } from 'vue';
import { currentUser } from "@/core/users/session";
import Button from "@/components/ui/button/Button.vue";
import ContextMenu from "@/components/ui/context-menu/ContextMenu.vue";
import Panel from "@/components/ui/panel/Panel.vue";
import Select from "@/components/ui/select/Select.vue";
import { ChevronDownIcon, FilterIcon } from "lucide-vue-next";
import { computed, onMounted, ref, watch } from "vue";
import { fetchGdaCentersKpi, fetchWorkingDaysProgress, GDA_KPI_TYPES, } from "../../api";
import BaseKpiChart from "../shared/BaseKpiChart.vue";
import { getGdaCentersKpiConfig } from "./config";
import { CHART_MAX_DECIMAL_PLACES, CHART_MIN_DECIMAL_PLACES, DEFAULT_GDA_KPI_TYPE } from "../../config";
export default /*@__PURE__*/ _defineComponent({
    __name: 'GdaCentersKpiPanel',
    props: {
        currentPeriodLabel: { type: String, required: false }
    },
    setup(__props, { expose: __expose }) {
        __expose();
        // State
        const isLoading = ref(true);
        const error = ref(null);
        const gdaKpiData = ref(null);
        const workingDaysProgress = ref(null);
        const selectedType = ref(DEFAULT_GDA_KPI_TYPE);
        const selectedDistributors = ref([]);
        const distributorSourceData = ref([]);
        // Computed
        const currentConfig = computed(() => getGdaCentersKpiConfig(selectedType.value));
        const showDistributorSelect = computed(() => selectedType.value === "distribuidor");
        const availableTypes = computed(() => {
            const isLevanteRegionUser = currentUser().region_code === "E300";
            return isLevanteRegionUser
                ? GDA_KPI_TYPES.filter((type) => type.value !== "delegation")
                : GDA_KPI_TYPES;
        });
        const distributorOptions = computed(() => {
            if (selectedType.value !== "distribuidor") {
                return [];
            }
            return distributorSourceData.value
                .map((item) => { var _a; return ((_a = item.distribuidor) === null || _a === void 0 ? void 0 : _a.trim()) || "(Sin nombre)"; })
                .filter((value, index, values) => value.length > 0 && values.indexOf(value) === index)
                .sort((left, right) => left.localeCompare(right, "es", { sensitivity: "base" }))
                .map((distributor) => ({
                label: distributor,
                value: distributor,
            }));
        });
        const typeDisplayText = computed(() => {
            const currentType = availableTypes.value.find((type) => type.value === selectedType.value);
            return currentType ? currentType.label : "Tipo";
        });
        const typeMenuOptions = computed(() => {
            return availableTypes.value.map((type) => ({
                label: type.label,
                value: type.value,
            }));
        });
        const isEmptyData = computed(() => {
            if (!gdaKpiData.value)
                return true;
            if (Array.isArray(gdaKpiData.value)) {
                return gdaKpiData.value.length === 0;
            }
            return false;
        });
        // Methods
        const handleTypeSelect = (option) => __awaiter(this, void 0, void 0, function* () {
            selectedType.value = option.value;
            yield loadData();
        });
        const loadData = () => __awaiter(this, void 0, void 0, function* () {
            try {
                isLoading.value = true;
                error.value = null;
                const [workingDaysResponse, gdaResponse] = yield Promise.all([
                    fetchWorkingDaysProgress(),
                    fetchGdaCentersKpi({
                        filter: selectedType.value,
                        distributors: selectedType.value === "distribuidor" ? selectedDistributors.value : undefined,
                    }),
                ]);
                workingDaysProgress.value = workingDaysResponse;
                gdaKpiData.value = gdaResponse;
                if (selectedType.value === "distribuidor" && selectedDistributors.value.length === 0 && Array.isArray(gdaResponse)) {
                    distributorSourceData.value = gdaResponse;
                }
                if (selectedType.value !== "distribuidor") {
                    distributorSourceData.value = [];
                }
            }
            catch (err) {
                error.value = "Error al cargar los datos de GDA Centers KPI";
                console.error("Error loading GDA Centers KPI data:", err);
            }
            finally {
                isLoading.value = false;
            }
        });
        // Lifecycle
        onMounted(() => __awaiter(this, void 0, void 0, function* () {
            yield loadData();
        }));
        watch(availableTypes, (types) => {
            if (types.some((type) => type.value === selectedType.value)) {
                return;
            }
            selectedType.value = DEFAULT_GDA_KPI_TYPE;
        });
        watch(showDistributorSelect, (isDistributorView) => {
            if (!isDistributorView && selectedDistributors.value.length > 0) {
                selectedDistributors.value = [];
            }
        });
        watch(selectedDistributors, () => __awaiter(this, void 0, void 0, function* () {
            if (!showDistributorSelect.value) {
                return;
            }
            yield loadData();
        }), { deep: true });
        watch(distributorOptions, (options) => {
            if (selectedDistributors.value.length === 0) {
                return;
            }
            const validValues = new Set(options.map((option) => option.value));
            selectedDistributors.value = selectedDistributors.value.filter((value) => validValues.has(value));
        });
        const __returned__ = { isLoading, error, gdaKpiData, workingDaysProgress, selectedType, selectedDistributors, distributorSourceData, currentConfig, showDistributorSelect, availableTypes, distributorOptions, typeDisplayText, typeMenuOptions, isEmptyData, handleTypeSelect, loadData, Button, ContextMenu, Panel, Select, get ChevronDownIcon() { return ChevronDownIcon; }, get FilterIcon() { return FilterIcon; }, BaseKpiChart, get CHART_MAX_DECIMAL_PLACES() { return CHART_MAX_DECIMAL_PLACES; }, get CHART_MIN_DECIMAL_PLACES() { return CHART_MIN_DECIMAL_PLACES; } };
        Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true });
        return __returned__;
    }
});
